import {
  Box,
  Button,
  Container,
  SpaceBetween,
} from '@cloudscape-design/components';
import { OrderStatus } from '@parallel-fluidics/shared/constants';
import { projectFromFirestore } from '@parallel-fluidics/shared/models/projects';
import { formatDate } from '@parallel-fluidics/shared/shipping';
import { pdf } from '@react-pdf/renderer';
import shipTravelersInventory from 'features/inventory/apis/shipTravelersInventory';
import { useNotifications } from 'features/notifications';
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  loadFromStorage,
  removeFromStorage,
  removeStaleFromStorage,
  saveToStorage,
} from 'utils';

import PDFPreviewPage from './pdfPreview/pdfPreviewPage';
import { useShipping } from './shippingProvider';

const PackageContext = createContext();

const DRAFT_KEY_PREFIX = 'createPackageDraft:';
// Flashbar id of the "restored draft" notice — exported so the page can hide
// it outside the packing-info step.
export const DRAFT_NOTIFICATION_ID = 'create-package-draft';
// Drafts older than this are discarded on load rather than restored.
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Build the initial per-package line items for a shipment. Qty Shipped
 * defaults to the remaining unshipped quantity; user can still override.
 */
function buildPackages(shipment, pkgNum) {
  return new Array(pkgNum).fill({}).map(() => ({
    lineItems: shipment.lineItems.map((item) => ({
      id: item.id,
      isChecked: true,
      name: item.name || item.projectId,
      projectId: item.projectId,
      description: item.description || item.desc,
      quantity: item.quantity,
      quantityShipped: Math.max(0, item.quantity - (item.quantityShipped || 0)),
      travelerIds: [],
    })),
  }));
}

/**
 * Overlay a cached draft's user-entered fields onto freshly built packages,
 * matching line items by id so items added to or removed from the order since
 * the draft was saved are handled gracefully (new items keep defaults, deleted
 * items are dropped).
 */
function overlayDraftPackages(freshPackages, draftPackages = []) {
  return freshPackages.map((pkg, idx) => {
    const draftItems = new Map(
      (draftPackages[idx]?.lineItems ?? []).map((li) => [li.id, li]),
    );
    return {
      lineItems: pkg.lineItems.map((li) => {
        const draftItem = draftItems.get(li.id);
        return draftItem ? { ...li, ...draftItem } : li;
      }),
    };
  });
}

export default function PackageProvider({ children }) {
  const { addNotification, removeNotification } = useNotifications();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const defaultShipIdx = searchParams.get('shipIdx');
  const navigate = useNavigate();
  const { toAddress, setToAddress } = useShipping();

  // fetch order based on orderId
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!orderId) return undefined;
    const unsubscribe = onSnapshot(
      doc(getFirestore(), 'orders', orderId),
      (orderDoc) => {
        setOrder({ id: orderDoc.id, ...orderDoc.data() });
        setStatus('success');
      },
      () => {
        setStatus('error');
      },
    );

    return () => unsubscribe();
  }, [orderId]);

  const [selectedShipment, setSelectedShipment] = useState(null);
  const [totalPkgNum, setTotalPkgNum] = useState(1);
  const [packages, setPackages] = useState([]);
  const [shipDate, setShipDate] = useState(formatDate(new Date()));
  const [shipMethod, setShipMethod] = useState('');
  const [stripeQuoteNumber, setStripeQuoteNumber] = useState('');
  const [qboEstimateDoc, setQboEstimateDoc] = useState('');
  const [quoteMap, setQuoteMap] = useState({});
  const [packagesData, setPackagesData] = useState([]);
  const [currentPkgNum, setCurrentPkgNum] = useState(1);

  useEffect(() => {
    if (!order) return;
    let firstNoneShipped = null;
    order.shipments.forEach((s, idx) => {
      if (defaultShipIdx && idx === parseInt(defaultShipIdx, 10)) {
        firstNoneShipped = { ...s, index: idx };
      } else if (!s.isShipped && !firstNoneShipped) {
        firstNoneShipped = { ...s, index: idx };
      }
    });
    const shipment = firstNoneShipped || { ...order.shipments[0], index: 0 };
    setSelectedShipment(shipment);
    setShipMethod(shipment.shippingMethod?.replace(/ \(\$.*$/, ''));

    if (order.qboEstimateDoc) { // if it's QBO estimate, we need to set the qboEstimateDoc
      setQboEstimateDoc(order.qboEstimateDoc);
    } else if (order.stripeId?.startsWith('in_')) { // if it's invoice, we need to get the quote number from stripe
      const getStripeQuoteId = async () => {
        const getQuoteId = httpsCallable(getFunctions(), 'getQuoteByInvoiceIdV2');
        try {
          const { data: quoteId } = await getQuoteId({ invoiceId: order.stripeId });
          setStripeQuoteNumber(quoteId);
        } catch (error) {
          setStripeQuoteNumber('');
          // eslint-disable-next-line no-console
          console.error('Error fetching quoteId', error);
        }
      };
      getStripeQuoteId();
    } else {
      // else we need to create a projectId to quoteId mapping
      const createItemQuoteMap = async () => {
        const projectIdsSet = new Set();
        order.shipments.forEach((s) => {
          s.lineItems.forEach((item) => {
            projectIdsSet.add(item.projectId);
          });
        });

        // Query Firestore for project
        const projectIdsArr = Array.from(projectIdsSet);
        const projectsQuery = query(collection(getFirestore(), 'projects'), where('name', 'in', projectIdsArr));
        const querySnapshot = await getDocs(projectsQuery);

        // Create a map of project IDs to partIds(Quotes)
        const projectIdQuoteMap = {};
        querySnapshot.forEach((snapshot) => {
          const project = projectFromFirestore(snapshot);
          if (project.partId) {
            projectIdQuoteMap[project.name] = project.partId.substring(0, 6).toUpperCase();
          }
        });

        setQuoteMap(projectIdQuoteMap);
      };

      createItemQuoteMap();
    }
  }, [order, defaultShipIdx]);

  useEffect(() => {
    if (!selectedShipment) return;
    setToAddress(selectedShipment.addressData);
  }, [selectedShipment, setToAddress]);

  useEffect(() => {
    if (currentPkgNum > totalPkgNum && totalPkgNum > 0) {
      setCurrentPkgNum(totalPkgNum);
    }
  }, [currentPkgNum, totalPkgNum]);

  // ── Draft persistence ──────────────────────────────────────────────────────
  // User-entered form state (package count, ship date, per-item qty/checked/
  // scanned travelers) is cached in localStorage so an accidental reload
  // doesn't lose it. Cleared on successful upload or explicit discard.
  const draftKey = orderId && selectedShipment
    ? `${DRAFT_KEY_PREFIX}${orderId}:${selectedShipment.index}`
    : null;

  // Sweep expired drafts from ALL orders/shipments once per mount — per-key
  // expiry only runs when that exact page is revisited, so abandoned drafts
  // would otherwise linger in localStorage forever.
  useEffect(() => {
    removeStaleFromStorage(DRAFT_KEY_PREFIX, DRAFT_MAX_AGE_MS);
  }, []);
  // True once the user has edited the form — the save effect only persists
  // real input, never the pristine initial build.
  const isDirtyRef = useRef(false);
  // Latest draft (loaded or saved) — re-applied whenever packages rebuild.
  const draftRef = useRef(null);
  const hydratedKeyRef = useRef(null);
  // Read via ref inside discardDraft so the callback identity stays stable —
  // the flashbar's Discard button captures it once at hydrate time, and
  // selectedShipment gets a new object identity on every order snapshot, so
  // closing over it directly would rebuild from the shipment as it was then.
  const selectedShipmentRef = useRef(null);
  useEffect(() => {
    selectedShipmentRef.current = selectedShipment;
  }, [selectedShipment]);

  const discardDraft = useCallback(() => {
    if (draftKey) removeFromStorage(draftKey);
    draftRef.current = null;
    isDirtyRef.current = false;
    removeNotification(DRAFT_NOTIFICATION_ID);
    setShipDate(formatDate(new Date()));
    setTotalPkgNum(1);
    const shipment = selectedShipmentRef.current;
    if (shipment) setPackages(buildPackages(shipment, 1));
  }, [draftKey, removeNotification]);

  // Hydrate once per draft key (initial load or shipment switch).
  useEffect(() => {
    if (!draftKey) return;
    if (hydratedKeyRef.current === draftKey) return;
    hydratedKeyRef.current = draftKey;
    isDirtyRef.current = false;
    draftRef.current = null;

    const draft = loadFromStorage(draftKey, null);
    // Coerce to a number — a corrupted non-numeric savedAt would make the age
    // comparison NaN (always false) and restore the draft as if it were fresh.
    const savedAt = Number(draft?.savedAt) || null;
    if (!savedAt || Date.now() - savedAt > DRAFT_MAX_AGE_MS) {
      if (draft) removeFromStorage(draftKey);
      return;
    }
    // Minimal shape guard — a draft that doesn't fit the current form (saved
    // by an older code version, or corrupted) is dropped rather than hydrated
    // into an empty packing form under a "restored draft" banner.
    const pkgNum = Number(draft.totalPkgNum);
    if (!Number.isInteger(pkgNum) || pkgNum < 1
      || typeof draft.shipDate !== 'string'
      || !Array.isArray(draft.packages)) {
      removeFromStorage(draftKey);
      return;
    }
    // Store the coerced count so the rebuild effect's strict-equality check
    // (draft.totalPkgNum === totalPkgNum) can't miss on a numeric string.
    draftRef.current = { ...draft, totalPkgNum: pkgNum };
    setTotalPkgNum(pkgNum);
    setShipDate(draft.shipDate);
    removeNotification(DRAFT_NOTIFICATION_ID);
    addNotification({
      id: DRAFT_NOTIFICATION_ID,
      type: 'info',
      dismissible: true,
      content: `Restored unsaved package draft from ${new Date(savedAt).toLocaleString()}.`,
      action: <Button onClick={discardDraft}>Discard draft</Button>,
    });
  }, [draftKey, addNotification, removeNotification, discardDraft]);

  useEffect(() => {
    if (!selectedShipment || !totalPkgNum) return;
    let pkgs = buildPackages(selectedShipment, totalPkgNum);
    // Re-apply the draft on every rebuild (reload, order snapshot update), as
    // long as the package count still matches what the draft was saved with —
    // changing the count rebuilds from scratch, same as before drafts existed.
    const draft = draftRef.current;
    if (draft && draft.totalPkgNum === totalPkgNum) {
      pkgs = overlayDraftPackages(pkgs, draft.packages);
    }
    setPackages(pkgs);
  }, [totalPkgNum, selectedShipment]);

  // Persist the draft after any user-initiated edit.
  useEffect(() => {
    if (!draftKey || !isDirtyRef.current) return;
    const draft = {
      savedAt: Date.now(),
      totalPkgNum,
      shipDate,
      packages: packages.map((pkg) => ({
        lineItems: pkg.lineItems.map((li) => ({
          id: li.id,
          isChecked: li.isChecked,
          quantityShipped: li.quantityShipped,
          travelerIds: li.travelerIds ?? [],
        })),
      })),
    };
    draftRef.current = draft;
    saveToStorage(draftKey, draft);
  }, [draftKey, packages, totalPkgNum, shipDate]);

  // User-initiated setters mark the draft dirty; internal rebuilds use the
  // raw setters so the pristine state is never persisted.
  const updatePackages = useCallback((value) => {
    isDirtyRef.current = true;
    setPackages(value);
  }, []);
  const updateTotalPkgNum = useCallback((value) => {
    isDirtyRef.current = true;
    setTotalPkgNum(value);
  }, []);
  const updateShipDate = useCallback((value) => {
    isDirtyRef.current = true;
    setShipDate(value);
  }, []);
  // ── End draft persistence ──────────────────────────────────────────────────

  const handleGetQuoteNum = useCallback((items) => {
    if (Object.keys(quoteMap).length === 0) return stripeQuoteNumber;
    const quote = [...new Set(items.map((item) => quoteMap[item.projectId]))].join(', ');
    return quote;
  }, [quoteMap, stripeQuoteNumber]);

  const generatePDFBlob = useCallback(async ({ pkgNum = 1, quoteNum, items = [] }) => {
    const document = pdf(
      <PDFPreviewPage
        order={order}
        toAddress={toAddress}
        renderItems={items}
        currentPkgNum={pkgNum}
        totalPkgNum={totalPkgNum}
        shipDate={shipDate}
        shipMethod={shipMethod}
        stripeQuoteNumber={quoteNum}
        qboEstimateNumber={qboEstimateDoc}
      />,
    );
    const blob = await document.toBlob();
    return blob;
  }, [
    order,
    toAddress,
    totalPkgNum,
    qboEstimateDoc,
    shipDate,
    shipMethod,
  ]);

  const uploadPDFToStorage = useCallback(async (blob, pkgNum, trackingCode) => {
    const storageRef = ref(getStorage(), `orders/${orderId}/packingSlip/${pkgNum}_${totalPkgNum}_${trackingCode}.pdf`);
    await uploadBytes(storageRef, blob, { contentType: 'application/pdf' });
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  }, [totalPkgNum, orderId]);

  const handleUpdateOrder = useCallback(async (trackingData) => {
    const shippedItems = trackingData.flatMap((pkg) => pkg.shippedItems);

    const updatedShipments = order.shipments.map((shipment, shipmentIdx) => {
      let isAllItemShipped = true;
      if (shipmentIdx !== selectedShipment.index) {
        return shipment;
      }
      const updatedLineItems = shipment.lineItems.map((lineItem) => {
        // A line item can be split across multiple packages — sum them all
        const foundShippedItems = shippedItems.filter((item) => item.id === lineItem.id);
        if (foundShippedItems.length === 0) {
          if (lineItem.quantity > (lineItem.quantityShipped || 0)) isAllItemShipped = false;
          return lineItem;
        }
        const shippedThisSession = foundShippedItems.reduce(
          (sum, item) => sum + (item.quantityShipped || 0),
          0,
        );
        const totalShipped = (lineItem.quantityShipped || 0) + shippedThisSession;
        // update lineItem's quantityShipped
        if (lineItem.quantity > totalShipped) isAllItemShipped = false;
        const updatedLineItem = {
          ...lineItem,
          quantityShipped: totalShipped,
        };

        return updatedLineItem;
      });

      return {
        ...shipment,
        lineItems: updatedLineItems,
        // mark shipment as shipped if all lineItems are shipped,
        // we leverage isShipped to hide/show the tracker info
        isShipped: isAllItemShipped,
        addressData: toAddress,
        trackingData: [...(shipment.trackingData || []), ...trackingData],
      };
    });

    const updateOrder = {
      shipments: updatedShipments,
      hasShippedItems: updatedShipments.some((shipment) => shipment.isShipped),
    };
    await updateDoc(doc(getFirestore(), 'orders', order.id), updateOrder);
  }, [order?.id, order?.shipments, selectedShipment?.index, toAddress]);

  const handleUpload = useCallback(async (shipments) => {
    try {
      const trackingData = await Promise.all(packages.map(async (pkg, index) => {
        const selectedItems = pkg.lineItems.filter((item) => item.isChecked);
        const quoteNum = handleGetQuoteNum(selectedItems);
        const pdfBlob = await generatePDFBlob({
          pkgNum: index + 1,
          quoteNum,
          items: selectedItems,
        });
        const shipment = shipments[index];
        const labelData = {
          trackingCode: shipment?.tracking_code || `manual_${shipment.id}`,
          trackingUrl: shipment?.tracker?.public_url || null,
          shipDate: shipment?.tracker?.created_at || new Date().toISOString(),
          carrier: shipment?.tracker?.carrier || null,
          shippingStatus: shipment?.tracker?.status || 'unknown',
          shippingStatusUpdatedAt: shipment?.tracker?.updated_at || new Date().toISOString(),
          shippingLabelUrl: shipment?.postage_label?.label_url || null,
          shipmentId: shipment.id,
          shippedItems: selectedItems.map(
            (item) => ({
              id: item.id,
              quantityShipped: item.quantityShipped,
              travelerIds: item.travelerIds ?? [],
            }),
          ),
          amount: shipment?.selected_rate?.rate || null,
        };
        const downloadURL = await uploadPDFToStorage(pdfBlob, index + 1, labelData.trackingCode);
        labelData.packingSlipUrl = downloadURL;
        return labelData;
      }));
      await handleUpdateOrder(trackingData);
      setPackagesData(trackingData);

      // The shipment is recorded on the order — the draft has served its purpose.
      if (draftKey) removeFromStorage(draftKey);
      draftRef.current = null;
      isDirtyRef.current = false;
      removeNotification(DRAFT_NOTIFICATION_ID);

      // Mark scanned travelers as shipped and record inventory (non-blocking).
      // Use trackingData directly — it already carries the right structure:
      //   package → { shipmentId, shippedItems: [{ id: lineItemId, travelerIds }] }
      // quantityShipped rides along so the tx reason can flag scan-vs-qty
      // mismatches for visibility.
      const inventoryPackages = trackingData
        .map((pkg) => ({
          shipmentId: pkg.shipmentId,
          lineItems: (pkg.shippedItems ?? [])
            .filter((item) => item.travelerIds?.length > 0)
            .map((item) => ({
              lineItemId: item.id,
              travelerIds: item.travelerIds,
              quantityShipped: item.quantityShipped,
            })),
        }))
        .filter((pkg) => pkg.lineItems.length > 0);

      if (inventoryPackages.length > 0) {
        shipTravelersInventory({
          packages: inventoryPackages,
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Error recording traveler shipment inventory:', err);
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading PDF:', error);
    }
  }, [
    packages,
    draftKey,
    removeNotification,
    handleUpdateOrder,
    handleGetQuoteNum,
    generatePDFBlob,
    uploadPDFToStorage,
  ]);

  const value = useMemo(() => ({
    order,
    selectedShipment,
    setSelectedShipment,
    totalPkgNum,
    setTotalPkgNum: updateTotalPkgNum,
    packages,
    setPackages: updatePackages,
    shipDate,
    setShipDate: updateShipDate,
    shipMethod,
    setShipMethod,
    packagesData,
    handleUpload,
    handleUpdateOrder,
    handleGetQuoteNum,
    qboEstimateDoc,
    currentPkgNum,
    setCurrentPkgNum,
  }), [
    order,
    selectedShipment,
    totalPkgNum,
    packages,
    shipDate,
    shipMethod,
    packagesData,
    currentPkgNum,
    qboEstimateDoc,
    handleUpload,
    handleUpdateOrder,
    handleGetQuoteNum,
    updatePackages,
    updateTotalPkgNum,
    updateShipDate,
  ]);

  if (order?.status !== OrderStatus.IN_PROGRESS && packagesData.length === 0) {
    return (
      <Container>
        <Box
          margin={{ vertical: 'xs' }}
          textAlign="center"
          color="inherit"
        >
          <SpaceBetween size="s">
            <div>This order is not in progress. Please check the order status.</div>
            <Button onClick={() => navigate(`/orders/update/${orderId}`)}>Back to order edit</Button>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <PackageContext.Provider value={value}>
      {!orderId || status === 'loading' || !order ? (
        <Container>{status === 'loading' ? 'Loading' : 'Invalid order ID or something went wrong'}</Container>
      ) : (
        children
      )}
    </PackageContext.Provider>
  );
}
PackageProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const usePackage = () => {
  const context = useContext(PackageContext);
  if (context === undefined) {
    throw new Error('usePackage must be used within a PackageProvider');
  }
  return context;
};
